import { z } from 'zod';
// ---------------------------------------------------------------------------
// Constantes de domínio
// ---------------------------------------------------------------------------
/** Índice 0 = Domingo … 6 = Sábado (mesma convenção de Date.getDay()) */
export const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
export const SACRAMENTOS = ['BATISMO', 'PRIMEIRA_EUCARISTIA', 'CRISMA'];
export const sacramentoSchema = z.enum(SACRAMENTOS);
export const SACRAMENTO_LABELS = {
    BATISMO: 'Batismo',
    PRIMEIRA_EUCARISTIA: 'Primeira Eucaristia',
    CRISMA: 'Crisma',
};
export const diaSemanaSchema = z.number().int().min(0).max(6);
/** Datas trafegam sempre como 'YYYY-MM-DD' (sem hora/fuso). */
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'data deve ser YYYY-MM-DD');
// ---------------------------------------------------------------------------
// Turma
// ---------------------------------------------------------------------------
export const turmaSchema = z.object({
    id: z.string().uuid(),
    nome: z.string().trim().min(1, 'nome é obrigatório'),
    diasSemana: z.array(diaSemanaSchema).min(1, 'escolha ao menos um dia').max(7),
    /**
     * Dia da semana (0=Dom … 6=Sáb) em que começa a "semana" (ciclo) da turma.
     * Default 1 (segunda) = semana ISO. Turmas cujo dia de reposição cai na
     * semana ISO seguinte (ex.: Sáb + Qua de reposição) começam o ciclo no dia
     * principal para que as duas datas caiam no mesmo encontro.
     */
    inicioCiclo: diaSemanaSchema.default(1),
    dataInicio: isoDateSchema,
    dataFim: isoDateSchema,
    metaPresenca: z.number().int().min(0).max(100),
});
export const createTurmaSchema = turmaSchema.omit({ id: true }).extend({
    /**
     * Professores vinculados à turma (N:N). Omitido no update mantém os vínculos
     * atuais; presente, substitui o conjunto. Catequista não-admin é sempre
     * incluído automaticamente ao criar uma turma.
     */
    catequistaIds: z.array(z.string().uuid()).max(100).optional(),
});
export const updateTurmaSchema = createTurmaSchema.partial();
// ---------------------------------------------------------------------------
// Aluno
// ---------------------------------------------------------------------------
export const alunoSchema = z.object({
    id: z.string().uuid(),
    nome: z.string().trim().min(1, 'nome é obrigatório'),
    idade: z.number().int().min(1).max(120).nullish(),
    telefone: z.string().trim().max(30).nullish(),
    sacramentos: z.array(sacramentoSchema).default([]),
    turmaId: z.string().uuid({ message: 'turma é obrigatória' }),
});
export const createAlunoSchema = alunoSchema.omit({ id: true });
export const updateAlunoSchema = createAlunoSchema.partial();
// ---------------------------------------------------------------------------
// Aula / Presença
// ---------------------------------------------------------------------------
export const lancarPresencaSchema = z.object({
    turmaId: z.string().uuid(),
    data: isoDateSchema,
    /** Anotações do catequista sobre a aula (ex.: "Parei o conteúdo em X"). */
    observacoes: z.string().trim().max(2000, 'máximo de 2000 caracteres').optional(),
    presencas: z
        .array(z.object({
        alunoId: z.string().uuid(),
        presente: z.boolean(),
    }))
        .min(1, 'informe ao menos um aluno'),
    /**
     * Trava otimista: versão da chamada que o cliente tinha ao abrir (`null` =
     * esperava que ainda não houvesse chamada nesta data). Se a chamada mudou
     * nesse meio-tempo, a API responde 409. Ausente = sem checagem.
     */
    versaoEsperada: z.number().int().min(0).nullable().optional(),
});
/** Edição posterior da anotação de uma chamada (compartilhada entre professores). */
export const updateObservacoesAulaSchema = z.object({
    /** String vazia limpa a observação. */
    observacoes: z.string().trim().max(2000, 'máximo de 2000 caracteres'),
});
// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export const ROLES = ['ADMIN', 'CATEQUISTA'];
export const roleSchema = z.enum(ROLES);
export const ROLE_LABELS = {
    ADMIN: 'Administrador',
    CATEQUISTA: 'Catequista',
};
export const loginSchema = z.object({
    email: z.string().trim().toLowerCase().email('e-mail inválido'),
    senha: z.string().min(1, 'senha é obrigatória'),
});
export const createCatequistaSchema = z.object({
    nome: z.string().trim().min(1, 'nome é obrigatório'),
    email: z.string().trim().toLowerCase().email('e-mail inválido'),
    senha: z.string().min(8, 'senha deve ter ao menos 8 caracteres'),
    role: roleSchema.default('CATEQUISTA'),
});
export const updateCatequistaSchema = z.object({
    nome: z.string().trim().min(1).optional(),
    email: z.string().trim().toLowerCase().email('e-mail inválido').optional(),
    /** Opcional: em branco/omitida mantém a senha atual. */
    senha: z.string().min(8, 'senha deve ter ao menos 8 caracteres').optional(),
    role: roleSchema.optional(),
    ativo: z.boolean().optional(),
});
//# sourceMappingURL=domain.js.map